import { Component, OnDestroy, OnInit, Type, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { CompletionService } from '../../services/completion.service';
import { ActiveTaskService } from '../../services/active-task.service';
import { TestInProgressComponent } from '../../guards/test-in-progress.guard';
import { LIVE_TOOLS, TOOL_BY_ID } from '../shell.models';
import { TOOL_COMPONENTS } from '../tools/tool-registry';

const EXERCISE_TOOLS = new Set([
  'bone',
  'steps',
  'sleepex',
  'kegel',
  'balance',
  'breathe',
]);

@Component({
  selector: 'app-tool-host',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './tool-host.component.html',
  styleUrl: './tool-host.component.scss',
})
export class ToolHostComponent
  implements OnInit, OnDestroy, TestInProgressComponent
{
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translation = inject(TranslationService);
  private completionSvc = inject(CompletionService);
  private activeTask = inject(ActiveTaskService);
  private sub?: Subscription;

  /**
   * F20: the tool screens are the ones that still show the bottom nav while a
   * check is running (the mapping flow hides it entirely, B2). The rendered tool
   * registers itself in ActiveTaskService for as long as its check/exercise is
   * on, and `testInProgressGuard` on this route turns a nav tap into the same
   * exit confirmation the tests use. Nothing is saved either way.
   */
  isTestInProgress(): boolean {
    return this.activeTask.isActive;
  }

  id = '';
  name = '';
  intro = '';
  disclaimerKey = 'shell.disclaimer.global';
  busy = false;
  known = false;
  // Resolved tool component (real, interactive tool). null → still loading.
  toolCmp: Type<unknown> | null = null;
  // A real tool is registered for this id (async-loading). Drives the stub:
  // stub shows ONLY when no loader exists, never during the import() flash.
  hasTool = false;

  ngOnInit(): void {
    this.sub = this.route.paramMap.subscribe((p) => {
      const id = p.get('id') || '';
      this.load(id);
    });
  }

  private load(id: string): void {
    this.id = id;
    this.known = !!TOOL_BY_ID[id];
    this.toolCmp = null;
    this.hasTool = false;

    // Delegate to the existing daily check-in component.
    if (LIVE_TOOLS.has(id) && id === 'diary') {
      this.router.navigate(
        ['/' + this.lang() + '/app/daily-checkin'],
        { replaceUrl: true },
      );
      return;
    }

    this.name = this.t('shell.tools.' + id + '.name');
    this.intro = this.t('shell.tools.' + id + '.intro');
    if (id === 'supps') this.disclaimerKey = 'shell.disclaimer.supps';
    else if (EXERCISE_TOOLS.has(id))
      this.disclaimerKey = 'shell.disclaimer.exercise';
    else this.disclaimerKey = 'shell.disclaimer.global';

    // Render the real tool when one is registered; else the "coming soon" stub.
    const loader = TOOL_COMPONENTS[id];
    this.hasTool = !!loader;
    if (loader) loader().then((cmp) => (this.toolCmp = cmp)).catch(() => {});
  }

  private t(key: string): string {
    return this.translation.translate(key);
  }
  private lang(): string {
    return this.translation.getCurrentLanguage();
  }

  back(): void {
    // Tool header back-arrow returns to the tools menu, not home (Danna 2026-06-18).
    // The post-exercise "back to dashboard" links (markDone) still go home on purpose.
    this.router.navigate(['/' + this.lang() + '/app/tools']);
  }

  async markDone(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.completionSvc.markDone(this.id);
    } catch (e) {
      console.error('markDone failed:', e);
    } finally {
      this.busy = false;
    }
    this.router.navigate(['/' + this.lang() + '/app/home']);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
